/**
 * Shared ICU MessageFormat resolver + message lookup — used by both client
 * (TranslationContext) and server (serverI18n) code.  No 'use client' /
 * 'use server' directive so it can be imported from either side of the
 * Next.js boundary.
 */
import IntlMessageFormat from 'intl-messageformat';
import { LOCALE_MESSAGES } from './i18n';

/** Module-level cache: Map<string, IntlMessageFormat> keyed by `${locale}\x00${message}` */
const fmtCache = new Map();

/**
 * Resolve ICU message string. If `values` is provided, parses as ICU
 * MessageFormat (with caching); otherwise returns the raw string.
 *
 * @param {string} locale
 * @param {string} message
 * @param {Record<string, any>} [values]
 * @returns {string | Array<string | object>}
 */
export function formatMessage(locale, message, values) {
  if (!values) return message;

  const cacheKey = `${locale}\x00${message}`;
  let fmt = fmtCache.get(cacheKey);
  if (!fmt) {
    fmt = new IntlMessageFormat(message, locale);
    fmtCache.set(cacheKey, fmt);
  }
  return fmt.format(values);
}

/**
 * Look up a translation key with graceful degradation:
 *   target locale → en-US → raw key string.
 *
 * Then format via ICU MessageFormat if values are provided.
 *
 * @param {string} locale
 * @param {string} key
 * @param {Record<string, any>} [values]
 * @returns {string | Array<string | object>}
 */
export function translateKey(locale, key, values) {
  const message = LOCALE_MESSAGES[locale]?.[key]
               ?? LOCALE_MESSAGES['en-US']?.[key]
               ?? key;
  return formatMessage(locale, message, values);
}
